<template>
  <Page :pagination="pagination">
    <template #content>
      <p
        class="page-title candidate_page_smaller_title"
        v-text="'extrait du rapport reçu par le talent'"
      />
      <div class="content-wrapper">
        <div class="candidate_pdf" data-convert="candidate_profile">
          <div class="candidate_pdf_block">
            <p class="title">VOS POINTS FORTS</p>
            <div
              v-for="(skill, index) in bestSkills"
              :key="index"
              class="candidate_pdf_top_block"
            >
              <div class="candidate_pdf_top_block_interior">
                <img
                  class="candidate_pdf_top_medal"
                  :src="`/images/resultsV2/medal_${index + 1}.svg`"
                />
                <div class="candidate_pdf_top_block_interior_right">
                  <div class="candidate_pdf_comp_title">
                    {{ getSkillWording(skill, "name") }}
                  </div>
                  <p class="candidate_pdf_comp_text">
                    {{ getSkillWording(skill, "short_description", true) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="candidate_pdf_block">
            <p class="title">VOTRE AXE D'AMÉLIORATION</p>
            <div class="candidate_pdf_top_block">
              <div class="candidate_pdf_top_block_interior">
                <img
                  class="candidate_pdf_top_medal"
                  :src="getLogo(worseSkill)"
                />
                <div class="candidate_pdf_top_block_interior_right">
                  <div class="candidate_pdf_comp_title">
                    Vous avez une marge de progression possible en
                    {{ getSkillWording(worseSkill, "name") }}
                  </div>
                  <p class="candidate_pdf_comp_text">
                    {{ getText(worseSkill) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="candidate_pdf_footnote">
          <div class="candidate_pdf_footnote_text">
            {{ $t("candidate_profiles.footnote_text") }}
          </div>
        </div>
      </div>
    </template>
  </Page>
</template>

<script setup>
import Page from "../Page.vue";
import { computed } from "vue";

const props = defineProps({
  pagination: {
    type: String,
    required: true,
  },
  results: {
    type: Object,
    required: true,
  },
  modules: {
    type: Array,
    required: true,
  },
});

// Define rank order for sorting (A is highest, E is lowest)
const rankOrder = { A: 5, B: 4, C: 3, D: 2, E: 1 };

const bestSkills = computed(() => {
  return Object.entries(props.results)
    .filter(([key, value]) => {
      return (
        props.modules.some(
          (mod) =>
            mod.id_v2 === key.replace("_score", "") ||
            mod.id === key.replace("_score", "")
        ) && value?.score
      );
    })
    .sort((a, b) => {
      // Sort primarily by rank (A > B > C > D > E)
      const rankDiff = rankOrder[b[1].rank] - rankOrder[a[1].rank];
      if (rankDiff !== 0) return rankDiff;
      
      // If ranks are the same, sort by score (higher score first)
      return b[1].score - a[1].score;
    })
    .slice(0, 3);
});

const worseSkill = computed(() => {
  return Object.entries(props.results)
    .filter(([key, value]) => {
      return (
        props.modules.some(
          (mod) =>
            mod.id_v2 === key.replace("_score", "") ||
            mod.id === key.replace("_score", "")
        ) && value?.score
      );
    })
    .sort((a, b) => {
      // Sort primarily by rank (A > B > C > D > E)
      const rankDiff = rankOrder[a[1].rank] - rankOrder[b[1].rank];
      if (rankDiff !== 0) return rankDiff;
      
      // If ranks are the same, sort by score (lower score first)
      return a[1].score - b[1].score;
    })[0];
});

function getSkillWording(skill, id, inResult = false) {
  const skillId = skill[0].replace("_score", "");

  const moduleIndex = props.modules.findIndex(
    (module) => module.id_v2 === skillId || module.id === skillId
  );

  return inResult
    ? props.modules[moduleIndex].result_wordings[id]
    : props.modules[moduleIndex][id];
}

function getLogo(skill) {
  const skillId = skill[0].replace("_score", "");

  const module = props.modules.find(
    (module) => module.id_v2 === skillId || module.id === skillId
  );

  const id = module.id_v2 || module.id;

  switch (id) {
    case "stress_manage":
      return "/images/resultsV2/Gestion-stress_icon_V2.svg";
    case "adaptability":
      return "/images/resultsV2/Adaptabilité_icon_V2.svg";
    case "pb_solving":
      return "/images/resultsV2/Resolution-probleme_icon_V2.svg";
    case "auto_efficient":
      return "/images/resultsV2/Confiance-compétence_icon_V2.svg";
    case "active_listen":
      return "/images/resultsV2/Ecoute-active_icon_V2.svg";
    case "empathy":
      return "/images/resultsV2/Empathie_icon_V2.svg";
    case "time_manage":
      return "/images/resultsV2/Gestion-temps_icon_V2.svg";
    case "planning":
      return "/images/resultsV2/Planification_icon_V2.svg";
    case "decisions":
      return "/images/resultsV2/Prise-décision_icon_V2.svg";
    default:
  }
}

function getText(skill) {
  const skillId = skill[0].replace("_score", "");

  const module = props.modules.find(
    (module) => module.id_v2 === skillId || module.id === skillId
  );

  const id = module.id_v2 || module.id;

  switch (id) {
    case "adaptability":
      return "Etre adaptable, c’est reconnaître et accepter le changement et revoir ses méthodes de travail si celles-ci ne collent plus au contexte. Dans un monde en mouvement constant, c’est une compétence clé pour évoluer de façon sereine dans un cadre professionnel.";
    case "auto_efficient":
      return "Avoir confiance en ses compétences, c'est reconnaître sa valeur et oser mettre en avant ses talents. C'est essentiel pour progresser et saisir les opportunités professionnelles.";
    case "active_listen":
      return "Pratiquer l'écoute active, c'est être pleinement présent et attentif aux messages verbaux et non-verbaux de ses interlocuteurs. C'est fondamental pour construire des relations professionnelles de qualité.";
    case "empathy":
      return "Être empathique, c'est comprendre et prendre en compte les émotions et le point de vue des autres. C'est une compétence clé pour le travail en équipe et le leadership.";
    case "time_manage":
      return "Gérer son temps efficacement, c'est savoir prioriser ses tâches et organiser son travail de manière optimale. C'est essentiel pour atteindre ses objectifs tout en maintenant un équilibre.";
    case "stress_manage":
      return "Savoir gérer son stress, c'est garder le contrôle de soi pour agir efficacement face à des situations de tension. C’est une compétence clé pour la plupart des postes où il faut savoir garder une approche rationnelle malgré un environnement qui peut être stressant.";
    case "planning":
      return "Planifier efficacement, c'est savoir organiser et séquencer les actions nécessaires pour atteindre un objectif. C'est fondamental pour la gestion de projet et l'atteinte des résultats.";
    case "decisions":
      return "Prendre des décisions, c'est savoir analyser une situation et choisir la meilleure option possible en tenant compte des contraintes. C'est une compétence clé pour tout poste à responsabilité.";
    case "pb_solving":
      return "Résoudre des problèmes, c’est la capacité à identifier et à traiter efficacement les problèmes ou les défis auxquels on est confronté. C'est une compétence essentielle pour comprendre les enjeux d’une situation et prendre des décisions rationnelles.";
    default:
  }
}
</script>

<style lang="scss" scoped>
.candidate_page_smaller_title {
  font-size: 29px !important;
}

.candidate_pdf {
  align-items: center;
  background-color: #1d1d1d;
  border: 2px solid;
  border-color: #eebf29;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  &_block {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .title {
      text-align: start;
      color: #ffffff;
      font-size: 16px;
      font-weight: 700;
      margin-bottom: 8px;
      text-transform: uppercase;
    }
  }

  &_top_block {
    background-color: #0f0e0e;
    border: 1px solid;
    border-color: #464646;
    padding: 4px;

    &_interior {
      display: flex;
      align-items: center;
      padding: 16px 8px;

      img {
        width: 75px;
        height: 75px;
      }

      &_right {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 85%;
        padding-left: 8px;
      }
    }
  }

  &_comp {
    &_title {
      color: #ffffff;
      font-family: "Bio Sans-Bold", Helvetica;
      font-size: 16px;
      font-weight: 600;
      text-align: left;
      margin-bottom: 8px;
    }

    &_text {
      color: #ffffff;
      font-family: "Bio Sans-Regular", Helvetica;
      font-size: 13px;
      line-height: 1.5;
      text-align: left;
      padding-top: 3px;
      opacity: 0.75;
    }
  }

  &_footnote {
    background-color: #fefdf6;
    border: 1px solid #f2cb36;
    box-sizing: border-box;
    padding: 10px;

    &_text {
      font-family: "Bio Sans-Regular", Helvetica;
      text-align: left;
      font-size: 15px;
    }
  }
}

.candidate_pdf_top_medal {
  position: relative;
  transform: scale(0.65);
}
</style>
